import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LineupsIcon } from '@sorare/core/src/atoms/icons/LineupsIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { EmptyState_so5Fixture } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true })`
  width: 100%;
  max-width: 400px;
  margin: auto;
  padding: calc(6 * var(--unit)) var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid rgba(var(--c-rgb-white), 0.07);
  background: rgba(var(--c-rgb-black), 0.19);
  backdrop-filter: blur(26px);
`;

const emptyState = defineMessages({
  pro: {
    id: 'fixture.EmptyState.goToNextGameWeek',
    defaultMessage: 'Go to next Game Week',
  },
  daily: {
    id: 'fixture.EmptyState.goToNextFixture',
    defaultMessage: 'Go to next Game day',
  },
});

type Props = {
  so5Fixture: EmptyState_so5Fixture;
  upcomingFixtureSlug: string;
};

export const EmptyState = ({ so5Fixture, upcomingFixtureSlug }: Props) => {
  const { canCompose } = so5Fixture;
  const { generateSportContextPath } = useSportContext();

  return (
    <Root gap={3}>
      <Chip>
        <FormattedMessage
          id="fixture.EmptyState.noLineupEntered"
          defaultMessage="No Lineups Entered"
        />
      </Chip>
      <Vertical center gap={2}>
        <LineupsIcon size={5} />
        <BodyL as="p" bold>
          {canCompose ? (
            <FormattedMessage
              id="fixture.EmptyState.exploreAvailableCompetitions"
              defaultMessage="Explore available competitions"
            />
          ) : (
            <FormattedMessage
              id="fixture.EmptyState.noLineupsPlaying"
              defaultMessage="No lineups playing"
            />
          )}
        </BodyL>
        <Button
          size="medium"
          color="primary"
          to={
            canCompose
              ? generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER,
                  {
                    params: {
                      eventType: so5Fixture.type.toLowerCase(),
                      fixture: so5Fixture.slug,
                    },
                  }
                )
              : generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
                  params: {
                    eventType: so5Fixture.type.toLowerCase(),
                    fixture: upcomingFixtureSlug,
                  },
                })
          }
        >
          {canCompose ? (
            <FormattedMessage {...fantasy.discoverCompetitions} />
          ) : (
            <FormattedMessage
              {...(so5Fixture.type === So5FixtureEvent.DAILY
                ? emptyState.daily
                : emptyState.pro)}
            />
          )}
        </Button>
      </Vertical>
    </Root>
  );
};

EmptyState.fragments = {
  so5Fixture: gql`
    fragment EmptyState_so5Fixture on So5Fixture {
      slug
      type
      canCompose
    }
  ` as TypedDocumentNode<EmptyState_so5Fixture>,
};
